<template>
    <!-- renderer SVG: el texto de la leyenda es DOM vectorial (no rasterizado al
        canvas), evita el recorte de píxeles del tope de las mayúsculas. -->
    <EChart v-if="chartReady" :option="chartOption" :init-options="{ renderer: 'svg' }" />
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import { parseToCompactMoney, parseToMoney } from "@/utils/currency";
import { interpolateHexPalette } from "@/utils/colorPalette";

// Misma familia cromática que SalesClientsStackedChart: los top N clientes se
// reparten el gradiente teal y "Otros" sale a su token propio. La diferencia
// es puramente de representación — acá una línea por cliente en lugar de
// barras apiladas, para leer la tendencia individual de cada uno.
const LIGHTEST_ANCHOR = "--report-chart-segment-3";
const DARKEST_ANCHOR = "--report-chart-segment-1";
const OTROS_ANCHOR = "--report-chart-other";
const TEXT_MUTED_ANCHOR = "--report-text-muted";
const TEXT_ANCHOR = "--report-text";
const BORDER_ANCHOR = "--report-border";

const OTROS_NAME = "Otros";
const LEGEND_NAME_MAX = 20;

export default {
    name: "SalesClientsLineChart",

    components: { EChart },

    props: {
        // Headers ya formateados (ej. ["abr 2024", "may 2024", ...]).
        monthHeaders: { type: Array, required: true },
        // Array paralelo a monthHeaders. Misma forma que el stacked chart:
        // [{ name, values[] }], con "Otros" (si existe) al final.
        series: {
            type: Array,
            required: true,
            validator: (arr) => arr.every((s) =>
                typeof s.name === "string" && Array.isArray(s.values)
            ),
        },
        // Toggle del popover del tooltip (conserva el axisPointer al apagarlo).
        showTooltip: { type: Boolean, default: true },
        // Modo porcentaje: cada línea pasa a ser el share del cliente sobre el
        // total de ventas del período (suma de todas las series, incl. Otros).
        // Eje + tooltip en %. Default false → montos absolutos.
        percentMode: { type: Boolean, default: false },
    },

    data() {
        return {
            chartReady: false,
            lightAnchor: "",
            darkAnchor: "",
            otrosColor: "",
            textColor: "",
            textMutedColor: "",
            borderColor: "",
        };
    },

    computed: {
        // "Otros" siempre al final (si existe); el resto en el orden del store.
        orderedSeries() {
            const top = this.series.filter((s) => s.name !== OTROS_NAME);
            const otros = this.series.find((s) => s.name === OTROS_NAME);
            return otros ? [...top, otros] : top;
        },

        topCount() {
            return this.orderedSeries.filter((s) => s.name !== OTROS_NAME).length;
        },

        // Total de ventas por período = suma de todas las series (top N + Otros)
        // en cada índice de mes. Es el denominador del % por cliente en
        // percentMode, y el "Total" que el tooltip muestra como contexto.
        columnTotals() {
            return this.monthHeaders.map((_, idx) =>
                this.orderedSeries.reduce((acc, s) => {
                    const v = s.values[idx];
                    return acc + (typeof v === "number" ? v : 0);
                }, 0),
            );
        },

        // Paleta hex por serie: N anchors del gradiente para los top + el token
        // de Otros como cierre. Idéntico criterio que el stacked para que ambos
        // charts del header usen los mismos colores por cliente.
        palette() {
            if (!this.lightAnchor || !this.darkAnchor) return [];
            const topPalette = this.topCount > 1
                ? interpolateHexPalette(this.darkAnchor, this.lightAnchor, this.topCount)
                : (this.topCount === 1 ? [this.darkAnchor] : []);
            const hasOtros = this.orderedSeries.some((s) => s.name === OTROS_NAME);
            return hasOtros ? [...topPalette, this.otrosColor] : topPalette;
        },

        chartOption() {
            const palette = this.palette;
            const percentMode = this.percentMode;
            const columnTotals = this.columnTotals;
            return {
                grid: {
                    top: 48,
                    right: 16,
                    bottom: 32,
                    left: 56,
                    containLabel: false,
                },
                legend: {
                    show: true,
                    // Una sola fila con scroll (deslizante de paginado): los
                    // clientes que no entran se navegan con las flechas, sin
                    // robarle alto al gráfico (vs. envolver a 2 líneas).
                    type: "scroll",
                    left: 0,
                    // `width` (no `right`, que el modo scroll ignora) acota la
                    // leyenda para dejar a la derecha el espacio de la toolbar
                    // superpuesta (2 botones: "% de ventas" + "Detalle"), en la
                    // misma fila, sin solaparse.
                    width: "60%",
                    top: 4,
                    itemGap: 16,
                    icon: "circle",
                    itemWidth: 8,
                    itemHeight: 8,
                    textStyle: {
                        color: this.textColor,
                        fontSize: 12,
                        fontWeight: 500,
                        // lineHeight ≥ fontSize: da alto de fila a la leyenda para
                        // que el clip vertical del modo scroll no recorte el tope
                        // del texto (itemHeight 8 < fontSize 12 lo dejaba corto).
                        lineHeight: 18,
                    },
                    formatter: (name) => (
                        name.length > LEGEND_NAME_MAX
                            ? `${name.slice(0, LEGEND_NAME_MAX)}…`
                            : name
                    ),
                },
                tooltip: {
                    trigger: "axis",
                    showContent: this.showTooltip,
                    confine: true,
                    extraCssText: "max-width:280px; white-space:normal; word-break:break-word;",
                    backgroundColor: "#0f172a",
                    borderColor: "#0f172a",
                    textStyle: { color: "#f8fafc", fontSize: 12 },
                    // Una fila por cliente (omitimos los que valen 0 en el mes
                    // hovereado para no inflar el tooltip) + total de la columna.
                    formatter: (params) => {
                        if (!Array.isArray(params) || params.length === 0) return "";
                        const header = `<div style="font-weight:600;margin-bottom:6px;">${params[0].axisValueLabel}</div>`;
                        // En percentMode el `value` ya es el share (%); mostramos
                        // el % por cliente + el total de ventas del período (en
                        // pesos) como contexto al pie.
                        if (percentMode) {
                            const idx = params[0].dataIndex;
                            const rows = params
                                .filter((p) => typeof p.value === "number" && p.value > 0)
                                .map((p) => `
                                    <div style="display:flex;justify-content:space-between;gap:12px;align-items:baseline;">
                                        <span style="min-width:0;overflow-wrap:anywhere;">${p.marker}${p.seriesName}</span>
                                        <span style="flex-shrink:0;">${p.value.toFixed(1)}%</span>
                                    </div>
                                `)
                                .join("");
                            return `${header}${rows}
                                <div style="border-top:1px solid rgba(255,255,255,0.15);margin-top:6px;padding-top:6px;display:flex;justify-content:space-between;gap:12px;">
                                    <span>Total ventas</span>
                                    <span style="flex-shrink:0;"><strong>${parseToMoney(columnTotals[idx] || 0)}</strong></span>
                                </div>`;
                        }
                        const total = params.reduce(
                            (acc, p) => acc + (typeof p.value === "number" ? p.value : 0),
                            0,
                        );
                        const rows = params
                            .filter((p) => typeof p.value === "number" && p.value !== 0)
                            .map((p) => `
                                <div style="display:flex;justify-content:space-between;gap:12px;align-items:baseline;">
                                    <span style="min-width:0;overflow-wrap:anywhere;">${p.marker}${p.seriesName}</span>
                                    <span style="flex-shrink:0;">${parseToMoney(p.value)}</span>
                                </div>
                            `)
                            .join("");
                        return `${header}
                            ${rows}
                            <div style="border-top:1px solid rgba(255,255,255,0.15);margin-top:6px;padding-top:6px;display:flex;justify-content:space-between;gap:12px;">
                                <span>Total</span>
                                <span style="flex-shrink:0;"><strong>${parseToMoney(total)}</strong></span>
                            </div>
                        `;
                    },
                },
                xAxis: {
                    type: "category",
                    data: this.monthHeaders,
                    boundaryGap: false,
                    axisLine: { lineStyle: { color: this.borderColor } },
                    axisTick: { show: false },
                    axisLabel: {
                        color: this.textMutedColor,
                        fontSize: 11,
                        margin: 12,
                    },
                },
                yAxis: {
                    type: "value",
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: {
                        lineStyle: { color: this.borderColor, type: "dashed" },
                    },
                    axisLabel: {
                        color: this.textMutedColor,
                        fontSize: 11,
                        formatter: (val) => (
                            percentMode ? `${Math.round(val)}%` : parseToCompactMoney(val)
                        ),
                    },
                },
                series: this.orderedSeries.map((s, i) => {
                    const color = palette[i] || this.darkAnchor;
                    // percentMode: cada valor → share del total del período
                    // (value / total * 100). Mes con total 0 → null (no dividir).
                    const data = percentMode
                        ? s.values.map((v, idx) => {
                            const total = columnTotals[idx];
                            return (typeof v === "number" && total > 0)
                                ? (v / total) * 100
                                : null;
                        })
                        : s.values;
                    return {
                        name: s.name,
                        type: "line",
                        smooth: true,
                        showSymbol: false,
                        lineStyle: { width: 2, color },
                        itemStyle: { color },
                        emphasis: { focus: "series" },
                        data,
                    };
                }),
            };
        },
    },

    mounted() {
        this.resolveCssColors();
        this.chartReady = Boolean(this.lightAnchor && this.darkAnchor && this.otrosColor);
    },

    methods: {
        resolveCssColors() {
            if (typeof window === "undefined") return;
            const root = getComputedStyle(document.documentElement);
            this.lightAnchor = root.getPropertyValue(LIGHTEST_ANCHOR).trim();
            this.darkAnchor = root.getPropertyValue(DARKEST_ANCHOR).trim();
            this.otrosColor = root.getPropertyValue(OTROS_ANCHOR).trim();
            this.textColor = root.getPropertyValue(TEXT_ANCHOR).trim();
            this.textMutedColor = root.getPropertyValue(TEXT_MUTED_ANCHOR).trim();
            this.borderColor = root.getPropertyValue(BORDER_ANCHOR).trim();
        },
    },
};
</script>

<style scoped lang="scss">
:deep(.echart) {
    width: 100%;
    height: 100%;
}
</style>
